// ABOUTME: Lightweight hook that fetches business days from the Delivery Window configuration.
// ABOUTME: Reuses the deliveryWindowConfig query cache — no extra API call if already fetched.

import {useMemo} from 'react'
import {useQuery} from '@tanstack/react-query'

import {api} from '../../../../App.store'
import {DAY_CODE_TO_ID, DAYS, DayCode, DayIdentifier, DeliveryWindowApiResponse} from '../types/deliveryWindow'

export const useBusinessDays = (
  countryId: string | undefined,
  businessLine: string | undefined,
  scopeId?: string | undefined,
  rootScopeId?: string | undefined,
): {businessDays: DayIdentifier[]; nonBusinessDays: DayIdentifier[]; isLoading: boolean} => {
  const {data: rawData, isLoading} = useQuery<DeliveryWindowApiResponse[], Error>({
    queryKey: ['deliveryWindowConfig', countryId, businessLine],
    queryFn: async () => {
      const res = await api.get<DeliveryWindowApiResponse[]>('/tuc-configs/delivery-windows', {
        params: {countryId, businessLine: businessLine!.toUpperCase()},
      })
      return res.data ?? []
    },
    enabled: Boolean(countryId && businessLine),
    staleTime: 30_000,
  })

  const businessDays = useMemo<DayIdentifier[]>(() => {
    if (!rawData || rawData.length === 0) return DAYS // default: all days are business days
    // Resolve business days for the currently selected scope: prefer its own override,
    // otherwise inherit from the root/country-default scope (mirrors useDeliveryWindowConfig).
    const resolved =
      (scopeId ? rawData.find((r) => r.scopeId === scopeId) : undefined) ??
      (rootScopeId ? rawData.find((r) => r.scopeId === rootScopeId) : undefined) ??
      rawData[0]
    const activeDays = resolved?.businessDays?.value?.activeDays ?? []
    const mapped = activeDays
      .map((code) => DAY_CODE_TO_ID[code as DayCode])
      .filter((d): d is DayIdentifier => Boolean(d))
    return mapped.length > 0 ? mapped : DAYS
  }, [rawData, scopeId, rootScopeId])

  const nonBusinessDays = useMemo<DayIdentifier[]>(
    () => DAYS.filter((d) => !businessDays.includes(d)),
    [businessDays],
  )

  return {businessDays, nonBusinessDays, isLoading}
}
